import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios"; import { handleAuthError } from "../../utils";
import withAuth from "../withAuth";
import { 
  Col, 
  Container, 
  Row, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  Card, 
  CardBody, 
  Badge, 
  Input,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from '../../Network';
import { usePagination } from "../../hooks/usePagination";
import PaginationControls from "../../components/Common/PaginationControls";
import { useConfirm } from "../../contexts/ConfirmContext";
import * as XLSX from "xlsx";
import AttendanceMatrix from "../../components/Common/AttendanceMatrix";
import "./faculty.scss";

const ManageFaculty = () => {
  const { showConfirm } = useConfirm();
  document.title = "Manage Faculty";

  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherPrograms, setTeacherPrograms] = useState([]);
  const [viewModal, setViewModal] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCohort, setSelectedCohort] = useState("none");
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(false);
  const [activeTab, setActiveTab] = useState("teachers");
  const [auditPrograms, setAuditPrograms] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilterCohort, setAuditFilterCohort] = useState("");
  const [auditFilterTeacher, setAuditFilterTeacher] = useState("");
  const [notFinishedCohortId, setNotFinishedCohortId] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [notFinishedDropdownOpen, setNotFinishedDropdownOpen] = useState(false);

  useEffect(() => {
    fetchCohorts();
    if (selectedCohort !== "none") {
      fetchTeachers();
    }
  }, [selectedCohort]);

  const fetchCohorts = async () => {
    try {
      const response = await axios.get(`${baseUrl}/faculty/cohort/all`);
      setCohorts(response.data.data);
    } catch (error) { handleAuthError(error)
      console.error("Error fetching cohorts:", error);
    }
  };

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const endpoint = `${baseUrl}/faculty/teacher/all`;
      
      const response = await axios.get(endpoint);
      setTeachers(response.data.data);
      setLoading(false);
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const fetchTeacherPrograms = async (teacherId) => {
    try {
      setIsLoadingPrograms(true);
      const response = await axios.get(`${baseUrl}/faculty/teacher/programs/${Number(selectedCohort)}/${teacherId}`);
      setTeacherPrograms(response.data.data);
      setIsLoadingPrograms(false);
    } catch (error) { handleAuthError(error)
      console.error("Error fetching teacher programs:", error);
      setTeacherPrograms([]);
      setIsLoadingPrograms(false);
    }
  };

  const handleViewTeacher = async (teacher) => {
    setSelectedTeacher(teacher);
    await fetchTeacherPrograms(teacher.id);
    setViewModal(true);
  };

  const toggleViewModal = () => {
    setViewModal(!viewModal);
  };

  const toggleInviteModal = () => {
    setInviteModalOpen(!inviteModalOpen);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredTeachers = teachers.filter(teacher => {
    const searchString = `${teacher.firstName} ${teacher.lastName} ${teacher.email}`.toLowerCase();
    return searchString.includes(searchQuery.toLowerCase());
  });

  const {
    paginatedData: paginatedTeachers,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    showAll,
    handlePageChange,
    handlePageSizeChange,
    goToNextPage,
    goToPreviousPage,
    canPreviousPage,
    canNextPage,
    paginatedDataLength,
  } = usePagination(filteredTeachers, 20);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCohortChange = (e) => {
    setSelectedCohort(e.target.value);
  };

  const fetchAuditPrograms = async () => {
    try {
      setAuditLoading(true);
      const params = new URLSearchParams();
      if (auditFilterCohort && Number(auditFilterCohort) > 0) params.set("cohortId", auditFilterCohort);
      if (auditFilterTeacher && Number(auditFilterTeacher) > 0) params.set("teacherId", auditFilterTeacher);
      const url = `${baseUrl}/faculty/audit/programs${params.toString() ? "?" + params.toString() : ""}`;
      const response = await axios.get(url);
      setAuditPrograms(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      handleAuthError(error);
      console.error("Error fetching audit programs:", error);
      setAuditPrograms([]);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "audit" || activeTab === "attendance") fetchTeachers();
  }, [activeTab]);
  useEffect(() => {
    if (activeTab === "audit") fetchAuditPrograms();
  }, [activeTab, auditFilterCohort, auditFilterTeacher]);

  const auditListFiltered = (() => {
    let list = auditPrograms;
    if (notFinishedCohortId && Number(notFinishedCohortId) > 0) {
      list = list.filter(
        (p) =>
          Number(p.cohortId) === Number(notFinishedCohortId) &&
          p.reportStatus === "Not Completed"
      );
    }
    return list;
  })();

  const handleProgramCompleteToggle = async (program) => {
    const newStatus = !program.isCompleted;
    const confirmed = await showConfirm({
      title: newStatus ? "Complete Program" : "Open Program",
      message: newStatus
        ? `Are you sure you want to mark this program as complete for ${program.childName}?`
        : `Are you sure you want to open this program for ${program.childName}?`,
      variant: "primary",
    });
    if (!confirmed) return;
    try {
      setActionLoadingId(program.id);
      await axios.put(`${baseUrl}/faculty/program/${program.id}/complete`, { status: newStatus });
      toast.success(newStatus ? "Program marked as Complete" : "Program opened");
      fetchAuditPrograms();
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to update program");
    } finally {
      setActionLoadingId(null);
    }
  };

  const exportAuditToExcel = () => {
    const dataToExport = auditListFiltered.map((p, i) => ({
      "#": i + 1,
      "Parent Name": p.parentName,
      "Child Name": p.childName,
      "Teacher Name": p.teacherName,
      Cohort: p.cohortTitle || "—",
      Project: p.project,
      "Program Completed": p.programCompleted,
      "Report Status": p.reportStatus,
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Program Audit");
    const fileName = `program_audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("Export downloaded");
  };

  return (
    <React.Fragment>
      <div className="page-content pt-5 faculty-page">
        <Container>
          <Breadcrumbs title=" " breadcrumbItem="Manage Faculty" />
          <div className="faculty-hero">
            <h1 className="faculty-hero-title">Faculty Dashboard</h1>
            <p className="faculty-hero-sub">Manage teachers and audit program completion</p>
          </div>
          <Row>
            <Col lg="12">
              <Nav tabs className="faculty-tabs">
                <NavItem>
                  <NavLink
                    className={activeTab === "teachers" ? "active" : ""}
                    onClick={() => setActiveTab("teachers")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="ri-team-line me-2"></i> Teachers List
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === "audit" ? "active" : ""}
                    onClick={() => setActiveTab("audit")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="ri-file-list-3-line me-2"></i> Program Audit
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === "attendance" ? "active" : ""}
                    onClick={() => setActiveTab("attendance")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="ri-checkbox-multiple-line me-2"></i> Attendance
                  </NavLink>
                </NavItem>
              </Nav>

              {activeTab === "attendance" && (
                <Card>
                  <CardBody>
                    <p className="text-muted small mb-3">
                      Each paid student in one selected cohort, with that student's present, absent, rescheduled present, and rescheduled absent totals. Older attendance marks stay separate and are not copied into this report.
                    </p>
                    <AttendanceMatrix
                      reportUrl={`${baseUrl}/faculty/attendance/cohort`}
                      cohorts={Array.isArray(cohorts) ? cohorts : (cohorts?.data || [])}
                      teachers={Array.isArray(teachers) ? teachers : (teachers?.data || [])}
                    />
                  </CardBody>
                </Card>
              )}

              {activeTab === "teachers" && (
              <>
              <Row className="align-items-center mb-3">
                <Col md={6}>
                  <div className="faculty-section-header">
                    <div className="faculty-section-icon teachers-icon-bg">
                      <i className="ri-team-line"></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0">
                        Teachers List{" "}
                        <span className="text-muted fw-normal ms-2">({teachers.length})</span>
                      </h5>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3 faculty-filter-bar">
                    <div className="me-2">
                      <select 
                        className="form-select"
                        value={selectedCohort}
                        onChange={handleCohortChange}
                      >
                        <option value="none">All Cohorts</option>
                        {cohorts.map((cohort) => (
                          <option key={cohort.id} value={cohort.id}>
                            {cohort.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="search-box">
                      <Input
                        type="text"
                        className="form-control"
                        placeholder="Search teachers..."
                        value={searchQuery}
                        onChange={handleSearch}
                        disabled={selectedCohort === "none"}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </div>
                </Col>
              </Row>

              <Card className="faculty-card faculty-card-teachers">
                <CardBody>
                  {selectedCohort === "none" ? (
                    <div className="faculty-empty-state">
                      <div className="faculty-empty-icon"><i className="ri-team-line"></i></div>
                      <h5 className="faculty-empty-title">Select a cohort</h5>
                      <p className="faculty-empty-text">Please select a cohort above to view teachers.</p>
                    </div>
                  ) : loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted">Loading teachers...</p>
                    </div>
                  ) : filteredTeachers.length === 0 ? (
                    <div className="faculty-empty-state">
                      <div className="faculty-empty-icon"><i className="ri-user-search-line"></i></div>
                      <h5 className="faculty-empty-title">No teachers found</h5>
                      <p className="faculty-empty-text">No teachers found for this cohort.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-centered table-hover align-middle table-nowrap mb-0 faculty-table">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Country</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedTeachers.map((teacher, index) => {
                            const globalIndex = showAll 
                              ? index 
                              : (currentPage - 1) * pageSize + index;
                            return (
                              <tr key={teacher.id || index}>
                                <td>{globalIndex + 1}</td>
                              <td>
                                <h5 className="font-size-14 mb-1">
                                  {teacher.firstName} {teacher.lastName}
                                </h5>
                                <p className="text-muted mb-0">{teacher.gender}</p>
                              </td>
                              <td>{teacher.email}</td>
                              <td>{teacher.phone || 'N/A'}</td>
                              <td>{teacher.country || 'N/A'}</td>
                              <td>
                                <span className={teacher.status ? "faculty-badge-success" : "faculty-badge-danger"}>
                                  {teacher.status ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-view-teacher btn-sm"
                                  onClick={() => handleViewTeacher(teacher)}
                                >
                                  <i className="ri-eye-line me-1"></i> View
                                </button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {filteredTeachers.length > 0 && (
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      pageSize={pageSize}
                      showAll={showAll}
                      handlePageChange={handlePageChange}
                      handlePageSizeChange={handlePageSizeChange}
                      goToNextPage={goToNextPage}
                      goToPreviousPage={goToPreviousPage}
                      canPreviousPage={canPreviousPage}
                      canNextPage={canNextPage}
                      paginatedDataLength={paginatedDataLength}
                    />
                  )}
                </CardBody>
              </Card>
              </>
              )}

              {activeTab === "audit" && (
              <Card>
                <CardBody>
                  <Row className="align-items-center mb-3">
                    <Col md={6}>
                      <h5 className="card-title mb-0">Program Audit <span className="text-muted fw-normal">(paid students only)</span></h5>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                        <select
                          className="form-select form-select-sm"
                          style={{ minWidth: 140 }}
                          value={auditFilterCohort}
                          onChange={(e) => setAuditFilterCohort(e.target.value)}
                        >
                          <option value="">All Cohorts</option>
                          {cohorts.map((c) => (
                            <option key={c.id} value={c.id}>{c.title}</option>
                          ))}
                        </select>
                        <select
                          className="form-select form-select-sm"
                          style={{ minWidth: 160 }}
                          value={auditFilterTeacher}
                          onChange={(e) => setAuditFilterTeacher(e.target.value)}
                        >
                          <option value="">All Teachers</option>
                          {teachers.map((t) => (
                            <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>
                          ))}
                        </select>
                        <Dropdown isOpen={notFinishedDropdownOpen} toggle={() => setNotFinishedDropdownOpen(!notFinishedDropdownOpen)}>
                          <DropdownToggle color="warning" caret>
                            Not finished
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem onClick={() => { setNotFinishedCohortId(""); setNotFinishedDropdownOpen(false); }}>
                              Clear filter
                            </DropdownItem>
                            {cohorts.map((c) => (
                              <DropdownItem key={c.id} onClick={() => { setNotFinishedCohortId(c.id); setNotFinishedDropdownOpen(false); }}>
                                {c.title}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </Dropdown>
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => {
                            setNotFinishedCohortId("");
                            setAuditFilterCohort("");
                            setAuditFilterTeacher("");
                          }}
                        >
                          <i className="ri-refresh-line me-1"></i> Reset
                        </Button>
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => showConfirm({ title: "Export to Excel", message: "Export current program audit list to Excel?", variant: "primary" }).then((ok) => { if (ok) exportAuditToExcel(); })}
                          disabled={auditListFiltered.length === 0}
                        >
                          <i className="ri-file-excel-2-line me-1"></i> Export to Excel
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  {notFinishedCohortId ? (
                    <div className="faculty-audit-hint mb-3">
                      Showing students in the selected cohort whose report is not completed.
                    </div>
                  ) : null}
                  {auditLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" style={{ width: "2.5rem", height: "2.5rem" }} /> 
                      <p className="mt-2 text-muted">Loading audit...</p>
                    </div>
                  ) : auditListFiltered.length === 0 ? (
                    <div className="faculty-empty-state">
                      <div className="faculty-empty-icon"><i className="ri-file-list-3-line"></i></div>
                      <h5 className="faculty-empty-title">No programs found</h5>
                      <p className="faculty-empty-text">Try adjusting filters or reset.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-centered table-hover align-middle table-nowrap mb-0 faculty-table">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Parent Name</th>
                            <th>Child Name</th>
                            <th>Teacher Name</th>
                            <th>Cohort</th>
                            <th>Project</th>
                            <th>Program Completed</th>
                            <th>Report Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditListFiltered.map((p, i) => (
                            <tr key={p.id}>
                              <td>{i + 1}</td>
                              <td>{p.parentName}</td>
                              <td>{p.childName}</td>
                              <td>{p.teacherName}</td>
                              <td><span className="badge bg-light text-dark">{p.cohortTitle || "—"}</span></td>
                              <td>{p.project === "Yes" ? <span className="faculty-badge-success">Yes</span> : <span className="faculty-badge-danger">No</span>}</td>
                              <td>{p.programCompleted === "Yes" ? <span className="faculty-badge-success">Yes</span> : <span className="faculty-badge-warning">No</span>}</td>
                              <td>{p.reportStatus === "Completed" ? <span className="faculty-badge-success">Completed</span> : <span className="faculty-badge-warning">Not Completed</span>}</td>
                              <td>
                                <Button
                                  color={p.isCompleted ? "secondary" : "success"}
                                  size="sm"
                                  className="rounded-pill"
                                  disabled={actionLoadingId === p.id}
                                  onClick={() => handleProgramCompleteToggle(p)}
                                >
                                  {actionLoadingId === p.id ? "..." : p.isCompleted ? "Open Program" : "Complete Program"}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Teacher Details Modal */}
      <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg" className="faculty-page">
        <ModalHeader toggle={toggleViewModal}>
          <i className="ri-user-line me-2"></i> {selectedTeacher?.firstName} {selectedTeacher?.lastName}
        </ModalHeader>
        <ModalBody>
          {selectedTeacher && (
            <div className="teacher-details">
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <h6 className="text-primary fw-bold"><i className="ri-profile-line me-1"></i> Personal Information</h6>
                    <hr />
                    <p><strong>Name:</strong> {selectedTeacher.firstName} {selectedTeacher.lastName}</p>
                    <p><strong>Email:</strong> {selectedTeacher.email}</p>
                    <p><strong>Phone:</strong> {selectedTeacher.phone || 'N/A'}</p>
                    <p><strong>Gender:</strong> {selectedTeacher.gender}</p>
                    <p><strong>Country:</strong> {selectedTeacher.country || 'N/A'}</p>
                    <p><strong>Timezone:</strong> {selectedTeacher.timezone || 'N/A'}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <h6 className="text-primary fw-bold"><i className="ri-briefcase-line me-1"></i> Professional Information</h6>
                    <hr />
                    <p><strong>Qualification:</strong> {selectedTeacher.qualification || 'N/A'}</p>
                    <p><strong>Experience:</strong> {selectedTeacher.experience || 'N/A'}</p>
                    <p><strong>Class Link:</strong> 
                      {selectedTeacher.classLink ? (
                        <a href={selectedTeacher.classLink} target="_blank" rel="noopener noreferrer">
                          Join Class
                        </a>
                      ) : 'N/A'}
                    </p>
                    <p><strong>Document:</strong> 
                      {selectedTeacher.docUrl ? (
                        <a href={selectedTeacher.docUrl} target="_blank" rel="noopener noreferrer">
                          View Document
                        </a>
                      ) : 'N/A'}
                    </p>
                    <p><strong>Status:</strong> 
                      <span className={selectedTeacher.status ? "faculty-badge-success ms-2" : "faculty-badge-danger ms-2"}>
                        {selectedTeacher.status ? "Active" : "Inactive"}
                      </span>
                    </p>
                  </div>
                </Col>
              </Row>

              <div className="mt-4">
                <h6 className="text-primary fw-bold"><i className="ri-book-open-line me-1"></i> Assigned Programs ({teacherPrograms.length})</h6>
                <hr />
                {isLoadingPrograms ? (
                  <div className="text-center">
                    <Spinner size="sm" /> Loading programs...
                  </div>
                ) : teacherPrograms.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover faculty-table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Program</th>
                          <th>Level</th>
                          <th>Next Class</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherPrograms.map(program => (
                          <tr key={program.id}>
                            <td>
                              {program.child?.firstName} {program.child?.lastName}
                              <br />
                              <small className="text-muted">
                                Parent: {program.child?.parent?.firstName} {program.child?.parent?.lastName}
                              </small>
                            </td>
                            <td>{program.package?.name}</td>
                            <td>Level {program.level}</td>
                            <td>{formatDate(program.nextClassDate)}</td>
                            <td>
                              <span className={program.status ? "faculty-badge-success" : "faculty-badge-danger"}>
                                {program.status ? "Active" : "Inactive"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="alert alert-info border-0 shadow-sm">
                    <i className="ri-information-line me-2"></i> This teacher is not currently assigned to any programs.
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Invite Teacher Modal */}
      <Modal isOpen={inviteModalOpen} toggle={toggleInviteModal}>
        <ModalHeader toggle={toggleInviteModal}>Invite Teacher</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              className="form-control"
              placeholder="Enter teacher's email"
            />
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-light" onClick={toggleInviteModal}>
              Cancel
            </button>
            <button className="btn btn-primary">
              Send Invitation
            </button>
          </div>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(ManageFaculty);
